import {
  createContext,
  useContext,
  useId,
  type KeyboardEvent,
  type ReactNode,
} from "react";

interface TabsProps {
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
  ariaLabel?: string;
}

interface TabListProps {
  children: ReactNode;
}

interface TabProps {
  value: string;
  children: ReactNode;
}

interface TabPanelsProps {
  children: ReactNode;
}

interface TabPanelProps {
  value: string;
  children: ReactNode;
}

interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
  baseId: string;
  ariaLabel: string | undefined;
}

const TabsContext = createContext<TabsContextValue | null>(null);

export function Tabs({ value, onChange, children, ariaLabel }: TabsProps): JSX.Element {
  const baseId = useId();
  return (
    <TabsContext.Provider value={{ value, onChange, baseId, ariaLabel }}>
      <div>{children}</div>
    </TabsContext.Provider>
  );
}

export function TabList({ children }: TabListProps): JSX.Element {
  const tabs = useTabsContext();
  return (
    <div className="overflow-x-auto border-b border-border" role="tablist" aria-label={tabs.ariaLabel}>
      <div className="flex min-w-max gap-1" role="presentation">
        {children}
      </div>
    </div>
  );
}

export function Tab({ value, children }: TabProps): JSX.Element {
  const tabs = useTabsContext();
  const selected = tabs.value === value;
  return (
    <button
      id={tabId(tabs.baseId, value)}
      type="button"
      role="tab"
      aria-selected={selected}
      aria-controls={panelId(tabs.baseId, value)}
      tabIndex={selected ? 0 : -1}
      data-tab-value={value}
      onClick={() => tabs.onChange(value)}
      onKeyDown={(event) => handleTabKeyDown(event, tabs.onChange)}
      className={tabClassName(selected)}
    >
      {children}
    </button>
  );
}

export function TabPanels({ children }: TabPanelsProps): JSX.Element {
  return <div className="pt-4">{children}</div>;
}

export function TabPanel({ value, children }: TabPanelProps): JSX.Element {
  const tabs = useTabsContext();
  const selected = tabs.value === value;
  return (
    <section
      id={panelId(tabs.baseId, value)}
      role="tabpanel"
      aria-labelledby={tabId(tabs.baseId, value)}
      hidden={!selected}
    >
      {children}
    </section>
  );
}

function useTabsContext(): TabsContextValue {
  const context = useContext(TabsContext);
  if (context === null) throw new Error("Tabs components must be used inside Tabs");
  return context;
}

function handleTabKeyDown(
  event: KeyboardEvent<HTMLButtonElement>,
  onChange: (value: string) => void,
): void {
  const nextIndex = getNextTabIndex(event);
  if (nextIndex === null) return;
  event.preventDefault();
  const tabs = getTabButtons(event.currentTarget);
  const currentIndex = tabs.indexOf(event.currentTarget);
  if (tabs.length === 0 || currentIndex < 0) return;
  const nextTab = tabs[nextIndex(tabs.length, currentIndex)];
  if (nextTab === undefined) return;
  nextTab.focus();
  const nextValue = nextTab.dataset.tabValue;
  if (nextValue !== undefined) onChange(nextValue);
}

function getNextTabIndex(
  event: KeyboardEvent<HTMLButtonElement>,
): ((length: number, current: number) => number) | null {
  if (event.key === "ArrowRight") return (length, current) => (current + 1) % length;
  if (event.key === "ArrowLeft") return (length, current) => (current - 1 + length) % length;
  if (event.key === "Home") return () => 0;
  if (event.key === "End") return (length) => length - 1;
  return null;
}

function getTabButtons(current: HTMLButtonElement): HTMLButtonElement[] {
  const tabList = current.closest('[role="tablist"]');
  if (tabList === null) return [];
  return Array.from(tabList.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
}

function tabClassName(selected: boolean): string {
  return cx(
    "relative min-h-11 whitespace-nowrap px-4 py-3 text-sm font-medium transition",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)]",
    selected ? "text-primary" : "text-text-secondary hover:text-text-primary",
    selected && "after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:bg-primary",
  );
}

function tabId(baseId: string, value: string): string {
  return `${baseId}-${value}-tab`;
}

function panelId(baseId: string, value: string): string {
  return `${baseId}-${value}-panel`;
}

function cx(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}
